// ─────────────────────────────────────────────
//  Auth.js  —  Pure API-driven Authentication
//  No browser storage persistence — callers handle auth state
// ─────────────────────────────────────────────

import { useState, useEffect, useRef, useCallback } from 'react';
import AuthService from '../../../services/authService';
import UserService from '../../../services/userService';
import WalletService from '../../../services/walletService';
import ShoppingService from '../../../services/shoppingService';

export const OTP_LENGTH = 6;

// ── User Types ────────────────────────────────
export const USER_TYPES = [
  {
    key: "individual",
    label: "Individual",
    categoryLabel: "Personal",
    subtitle: "Apply for visas personally — solo or small group travel.",
    features: [
      "Apply for one or multiple visas easily",
      "Simple and quick guided process",
    ],
    showMembership: false,
  },
  {
    key: "member",
    label: "Member",
    categoryLabel: "Premium",
    subtitle: "For group or repeated visa applications at a premium tier.",
    features: [
      "Faster processing for repeat users",
      "Special pricing & priority support",
    ],
    showMembership: true,
  },
  {
    key: "agent",
    label: "Agent",
    categoryLabel: "Business",
    subtitle: "Registered agencies handling bulk visa applications.",
    features: [
      "Bulk visa application handling",
      "Dedicated agent dashboard",
    ],
    showMembership: false,
  },
];

// ── Login/Signup UI Strings ──────────────────
export const LOGIN_STRINGS = {
  MODAL_WELCOME_BACK: "Welcome Back",
  MODAL_CREATE_ACCOUNT: "Create Account",
  GOOGLE_LOGIN: "Continue with Google",
  GOOGLE_SIGNUP: "Sign up with Google",
  OR_DIVIDER: "or",
  LOGIN_EMAIL_LABEL: "Email Address",
  LOGIN_EMAIL_PLACEHOLDER: "you@example.com",
  LOGIN_PASSWORD_LABEL: "Password",
  LOGIN_PASSWORD_PLACEHOLDER: "••••••••",
  LOGIN_SUBMIT: "Sign In ",
  SIGNUP_NAME_LABEL: "Full Name",
  SIGNUP_NAME_PLACEHOLDER: "Your name",
  SIGNUP_COMPANY_LABEL: "Company Name",
  SIGNUP_COMPANY_PLACEHOLDER: "Your company name",
  SIGNUP_COUNTRY_LABEL: "Country",
  SIGNUP_COUNTRY_PLACEHOLDER: "e.g. India",
  SIGNUP_EMAIL_LABEL: "Email Address",
  SIGNUP_EMAIL_PLACEHOLDER: "you@example.com",
  SIGNUP_PASSWORD_LABEL: "Password",
  SIGNUP_PASSWORD_PLACEHOLDER: "Min 8 characters",
  SIGNUP_SUBMIT: "Create Account ",
  TOGGLE_NO_ACCOUNT: "Don't have an account?",
  TOGGLE_BUY_NOW: "Buy Now",
  TOGGLE_SIGN_UP: "Sign Up",
  TOGGLE_HAVE_ACCOUNT: "Already have an account?",
  TOGGLE_SIGN_IN: "Sign In",
  CARD_LOGIN_AS: "Login as",
  CARD_SIGN_UP: "Sign Up",
  CARD_BUY: "Buy",
  SUCCESS_SIGNUP_MEMBERSHIP: "Account created successfully! Membership activated.",
  SUCCESS_SIGNUP_DASHBOARD: "Account created successfully! Redirecting to dashboard...",
  ROLE_BADGE_ACCOUNT: " Account",
  ROLE_BADGE_MEMBERSHIP: " Membership",
  PAYMENT_TITLE: "Membership Payment",
  PAYMENT_BASE_PRICE: "Base Price",
  PAYMENT_GST: "GST (18%)",
  PAYMENT_TOTAL: "Total Amount",
  PAYMENT_CURRENCY: "\u20B9",
  PAYMENT_BILLING_TITLE: "Billing Details",
  PAYMENT_FIRST_NAME_LABEL: "First Name",
  PAYMENT_FIRST_NAME_PLACEHOLDER: "John",
  PAYMENT_LAST_NAME_LABEL: "Last Name",
  PAYMENT_LAST_NAME_PLACEHOLDER: "Doe",
  PAYMENT_PHONE_LABEL: "Phone Number",
  PAYMENT_PHONE_PLACEHOLDER: "9876543210",
  PAYMENT_SUBMIT: (total) => `Pay \u20B9${total} & Activate Membership`,
  CONSENT_TITLE: "Membership Consent & Authorization",
  CONSENT_INTRO: "By proceeding with this payment, I confirm that I voluntarily choose to become a member of TheVisaCo.",
  CONSENT_LIST_INTRO: "I understand and agree that:",
  CONSENT_BULLET_1: "The payment made is for membership enrollment and related services offered by TheVisaCo.",
  CONSENT_BULLET_2: "This membership may provide access to visa assistance tools, guidance, and related services as described on the platform.",
  CONSENT_BULLET_3: "I acknowledge that all payments are final unless otherwise stated in the refund policy.",
  CONSENT_BULLET_4_PREFIX: "I confirm that I have read and understood the ",
  CONSENT_BULLET_4_TERMS: "Terms & Conditions",
  CONSENT_BULLET_4_AND: " and ",
  CONSENT_BULLET_4_PRIVACY: "Privacy Policy",
  CONSENT_BULLET_4_SUFFIX: " of TheVisaCo.",
  CONSENT_BULLET_5: "I authorize TheVisaCo to process this payment for the selected membership plan.",
  CONSENT_BULLET_6: "I also confirm that I am providing accurate information and I am legally eligible to make this payment.",
  CONSENT_CHECKBOX_LABEL: "I agree to the Terms & Conditions and Consent Agreement",
  CONSENT_TERMS_URL: "/terms",
  CONSENT_PRIVACY_URL: "/privacy",
  RECEIPT_TITLE: "Payment Successful!",
  RECEIPT_SUCCESS_ICON: "\u2713",
  RECEIPT_SUCCESS_MSG: "Your payment has been processed successfully.",
  RECEIPT_BILL_TO: "Bill To",
  RECEIPT_TRANSACTION_ID: "Transaction ID",
  RECEIPT_DATE: "Date",
  RECEIPT_ACCOUNT_TYPE: "Account Type",
  RECEIPT_BASE_PRICE: "Base Price",
  RECEIPT_GST: "GST (18%)",
  RECEIPT_TOTAL_PAID: "Total Paid",
  RECEIPT_PAYMENT_METHOD: "Payment Method",
  RECEIPT_STATUS: "Status",
  RECEIPT_STATUS_PAID: "PAID",
  RECEIPT_DOWNLOAD_BTN: "Download Receipt (PDF)",
  RECEIPT_CREATE_ACCOUNT_BTN: "Create Account ",
  RECEIPT_ITEM_DATA: "Membership Plan",
  RECEIPT_ITEM_TYPE: "membership",
  HERO_TITLE: "Start Your Visa Journey",
  HERO_SUBTITLE: "Choose your account type below to get started",
  PAYMENT_METHOD_LABELS: {
    debit: "Debit Card",
    credit: "Credit Card",
    upi: "UPI",
    netbanking: "Net Banking",
  },
  DEFAULT_COUNTRY: "India",
  DEFAULT_PHONE_CODE: "+91",
  CARD_ENDING_IN: "ending in ",
  NA_FALLBACK: "N/A",
  TXN_PREFIX: "TXN",
  PDF_FILENAME_PREFIX: "membership_receipt_",
  PDF_GENERATION_ERROR: "PDF generation failed:",
  CURRENCY_CODE: "INR",
};

// ── Membership Payment Constants ──────────────
export const MEMBERSHIP_BASE_PRICE = 999;
export const MEMBERSHIP_GST_RATE = 0.18;
export const MEMBERSHIP_GST_AMOUNT = Math.round(MEMBERSHIP_BASE_PRICE * MEMBERSHIP_GST_RATE);
export const MEMBERSHIP_TOTAL_PRICE = MEMBERSHIP_BASE_PRICE + MEMBERSHIP_GST_AMOUNT;

// ── Signup ───────────────────────────────────
export async function signupUser({ name, email, password, role, companyName, country }) {
  const normalizedEmail = email.trim().toLowerCase();
  try {
    const result = await AuthService.signup({
      name, email: normalizedEmail, password, role,
      companyName: role === "agent" ? companyName : undefined,
      country: role === "agent" ? country : undefined,
    });
    console.log(`[API] OTP for ${normalizedEmail}: ${result.devOtp || '(check email)'}`);
    return { success: true, otp: result.devOtp, userId: result.user?.human_id, user: result.user, email: normalizedEmail };
  } catch (apiErr) {
    return { success: false, error: apiErr.response?.data?.message || "Signup failed. Please try again." };
  }
}

// ── Verify OTP ───────────────────────────────
export async function verifyOtp(email, inputOtp) {
  try {
    await AuthService.verifyOtp(email, inputOtp);
    return { success: true };
  } catch (apiErr) {
    return { success: false, error: apiErr.response?.data?.message || "OTP verification failed." };
  }
}

// ── Verify Email OTP & Complete Login ────────
export async function verifyEmailOtpAndLogin(email, password, otp) {
  const normalizedEmail = email.trim().toLowerCase();
  try {
    await AuthService.verifyOtp(normalizedEmail, otp);
    const result = await AuthService.login(normalizedEmail, password);
    const user = {
      id: result.user.id,
      human_id: result.user.human_id,
      name: result.user.name,
      email: result.user.email,
      role: result.user.role,
      authProvider: 'email',
    };
    return { success: true, user, accessToken: result.accessToken, refreshToken: result.refreshToken };
  } catch (apiErr) {
    return { success: false, error: apiErr.response?.data?.message || "Verification failed." };
  }
}

// ── Login ────────────────────────────────────
export async function loginUser({ email, password }) {
  const normalizedEmail = email.trim().toLowerCase();
  try {
    const result = await AuthService.login(normalizedEmail, password);
    const user = {
      id: result.user.id,
      human_id: result.user.human_id,
      name: result.user.name,
      email: result.user.email,
      role: result.user.role,
      authProvider: 'email',
    };
    return { success: true, user, accessToken: result.accessToken, refreshToken: result.refreshToken };
  } catch (apiErr) {
    return { success: false, error: apiErr.response?.data?.message || "Login failed." };
  }
}

// ── Google Sign-In (real API) ──
export async function googleSignIn(idToken, role, companyName, country) {
  try {
    const result = await AuthService.googleSignIn(idToken, role, companyName, country);
    return { success: true, user: result.user, isNew: result.isNew, rawResult: result };
  } catch (apiErr) {
    return { success: false, error: apiErr.response?.data?.message || "Google sign-in failed." };
  }
}

export async function completeGoogleSignup(rawResult) {
  const user = {
    id: rawResult.user.id,
    human_id: rawResult.user.human_id,
    name: rawResult.user.name,
    email: rawResult.user.email,
    role: rawResult.user.role,
    authProvider: 'google',
    hasPassword: false,
  };
  return { success: true, user, accessToken: rawResult.accessToken, refreshToken: rawResult.refreshToken };
}

// ── Verify Phone During Email Signup ─────────
export async function verifyPhoneForSignup({ phone, phoneCode, otp, email, password }) {
  try {
    const result = await AuthService.verifyPhoneOtp({
      phone, phoneCode, otp, purpose: "signup_verify", email, password,
    });
    const user = {
      id: result.user.id, human_id: result.user.human_id,
      name: result.user.name, email: result.user.email, role: result.user.role,
      authProvider: 'email',
    };
    return { success: true, user, accessToken: result.accessToken, refreshToken: result.refreshToken };
  } catch (apiErr) {
    return { success: false, error: apiErr.response?.data?.message || "Verification failed." };
  }
}

// ── Phone OTP ────────────────────────────────
export async function sendIdentityOtp() {
  try {
    await AuthService.sendIdentityEmailOtp();
    return { success: true };
  } catch (apiErr) {
    return { success: false, error: apiErr.response?.data?.message || "Failed to send identity OTP." };
  }
}

export async function sendPhoneOtpUser({ phone, phoneCode, purpose, name, role, companyName, country }) {
  try {
    const result = await AuthService.sendPhoneOtp({ phone, phoneCode, purpose, name, role, companyName, country });
    return { success: true };
  } catch (apiErr) {
    return { success: false, error: apiErr.response?.data?.message || "Failed to send OTP." };
  }
}

export async function verifyPhoneOtpUser({ phone, phoneCode, otp, purpose, name, role, companyName, country }) {
  try {
    const result = await AuthService.verifyPhoneOtp({ phone, phoneCode, otp, purpose, name, role, companyName, country });
    if (purpose === "verify_phone") {
      return { success: true };
    }
    const user = {
      id: result.user.id,
      human_id: result.user.human_id,
      name: result.user.name,
      email: result.user.email,
      role: result.user.role,
      authProvider: purpose === 'verify_phone' ? 'email' : 'phone',
    };
    return { success: true, user, accessToken: result.accessToken, refreshToken: result.refreshToken };
  } catch (apiErr) {
    return { success: false, error: apiErr.response?.data?.message || "OTP verification failed." };
  }
}

// ── Forgot / Reset Password ───────────────────
export async function forgotPasswordOtp(email) {
  try {
    const result = await AuthService.forgotPassword(email);
    return { success: true, message: result.message };
  } catch (apiErr) {
    return { success: false, error: apiErr.response?.data?.message || "Failed to send OTP." };
  }
}

export async function resetPassword(email, otp, newPassword) {
  try {
    const result = await AuthService.resetPassword(email, otp, newPassword);
    return { success: true, message: result.message };
  } catch (apiErr) {
    return { success: false, error: apiErr.response?.data?.message || "Password reset failed." };
  }
}

export async function resetPasswordByPhone(phone, phoneCode, otp, newPassword) {
  try {
    const result = await AuthService.resetPasswordByPhone(phone, phoneCode, otp, newPassword);
    return { success: true, message: result.message };
  } catch (apiErr) {
    return { success: false, error: apiErr.response?.data?.message || "Password reset failed." };
  }
}

export async function logout() {
  try {
    await AuthService.logout();
  } catch {
    // ignore logout errors
  }
}

// ── Documents ──────────────────────────────
export async function getDocuments() {
  try {
    const docs = await UserService.listDocuments();
    return docs || {};
  } catch {
    return {};
  }
}

// ── Wallet ─────────────────────────────────
export async function getRedeemableBalance() {
  try {
    const wallet = await WalletService.getWallet();
    return wallet.redeemable?.balance ?? 0;
  } catch {
    return 0;
  }
}
export async function getTotalWalletBalance() {
  try {
    const wallet = await WalletService.getWallet();
    return (wallet.redeemable?.balance ?? 0) + (wallet.nonRedeemable?.balance ?? 0);
  } catch {
    return 0;
  }
}
export async function getNonRedeemableBalance() {
  try {
    const wallet = await WalletService.getWallet();
    return wallet.nonRedeemable?.balance ?? 0;
  } catch {
    return 0;
  }
}
export async function hasEarnedRedeemable() {
  try {
    const wallet = await WalletService.getWallet();
    return (wallet.redeemable?.balance ?? 0) > 0;
  } catch {
    return false;
  }
}

// ── Shopping ───────────────────────────────
export async function getShopOrders(email) {
  try {
    const result = await ShoppingService.listOrders(email);
    return result.data || [];
  } catch { return []; }
}
export async function addShopOrder(email, order) {
  try {
    await ShoppingService.placeOrder({ ...order, email });
  } catch { /* non-critical */ }
}

// ── OTP Verification Hook (storage-free) ──────
export function useOtpVerification() {
  const [otp, setOtp]            = useState('');
  const [method, setMethod]      = useState(null);
  const [sent, setSent]          = useState(false);
  const [verified, setVerified]  = useState(false);
  const [sending, setSending]    = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [timer, setTimer]        = useState(120);
  const [timerActive, setTimerActive] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerActive && timer > 0) {
      timerRef.current = setTimeout(() => setTimer((t) => t - 1), 1000);
    } else if (timerActive && timer === 0) {
      setTimerActive(false);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [timerActive, timer]);

  const sendOtp = useCallback(async (otpMethod, target) => {
    setMethod(otpMethod);
    setSending(true);
    const res = otpMethod === 'email'
      ? await sendIdentityOtp()
      : await sendPhoneOtpUser({ ...target, purpose: 'verify_phone' });
    if (res.success) {
      setSent(true);
      setOtp('');
      setTimer(120);
      setTimerActive(true);
    }
    setSending(false);
    return res;
  }, []);

  const verifyOtpAction = useCallback(async (target) => {
    if (!method) return { success: false, error: 'No OTP method selected.' };
    setVerifying(true);
    const res = method === 'email'
      ? await verifyOtp(target, otp)
      : await verifyPhoneOtpUser({ ...target, otp, purpose: 'verify_phone' });
    if (res.success) {
      setVerified(true);
      setTimerActive(false);
    }
    setVerifying(false);
    return res;
  }, [method, otp]);

  const reset = useCallback(() => {
    setMethod(null);
    setSent(false);
    setVerified(false);
    setOtp('');
    setSending(false);
    setVerifying(false);
    setTimer(120);
    setTimerActive(false);
  }, []);

  return {
    otp, setOtp, method, sent, verified, sending, verifying,
    timer, timerActive, sendOtp, verifyOtp: verifyOtpAction, reset,
  };
}

